import { useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop';

import type { AttachmentUser, AttachmentVersionItem } from '../attachmentsMacroTypes.ts';

import type {
	useAttachmentVersionsQuery$data,
	useAttachmentVersionsQuery$variables,
} from './__generated__/useAttachmentVersionsQuery.graphql.ts';

/**
 * Lazy version-history request for a single attachment.
 *
 * Mirrors attachments-page's AttachmentHistoryLoader: queries
 * `contentVersionHistory` filtered to `contentType: attachment` and normalizes
 * the result to AttachmentVersionItem[] so VersionHistory renders unchanged.
 *
 * Fired ONLY when the row is expanded (the consumer passes `skip`) — 95% of
 * users never expand, so this avoids per-attachment pre-fetching on initial
 * load. Per-attachment cache key means re-expanding the same row is instant.
 */
export const USE_ATTACHMENT_VERSIONS_QUERY: GraphQLTaggedNode = graphql`
	query useAttachmentVersionsQuery($attachmentId: ID!, $filter: ContentVersionHistoryFilter!) {
		contentVersionHistory(id: $attachmentId, first: 100, filter: $filter) {
			nodes {
				number
				updatedTime
				author {
					id
					displayName
				}
				message
			}
		}
	}
`;

type UseAttachmentVersionsResult = {
	versions: AttachmentVersionItem[];
	loading: boolean;
	error: Error | null;
	refetch: (() => void) | undefined;
};

export const useAttachmentVersions = (
	attachmentId: string | undefined,
	skip: boolean = false,
): UseAttachmentVersionsResult => {
	const shouldSkip = skip || !attachmentId;

	const { data, loading, error, refetch } = useQueryInterop<
		useAttachmentVersionsQuery$data,
		useAttachmentVersionsQuery$variables
	>(USE_ATTACHMENT_VERSIONS_QUERY, {
		variables: {
			attachmentId: attachmentId ?? '',
			filter: { contentType: 'attachment' },
		},
		fetchPolicy: 'cache-and-network',
		skip: shouldSkip,
	});

	const versions = useMemo<AttachmentVersionItem[]>(() => {
		const rawNodes = data?.contentVersionHistory?.nodes ?? [];

		// Drop null nodes (DSP eviction / auth) and sort newest-first.
		const filtered = rawNodes.filter((n): n is NonNullable<typeof n> => Boolean(n));
		filtered.sort((a, b) => (b.number ?? 0) - (a.number ?? 0));

		return filtered.map((node, index): AttachmentVersionItem => {
			const modifier: AttachmentUser = {
				displayName: node.author?.displayName ?? '',
				accountId: node.author?.id ?? '',
				// profilePicture is required by AttachmentUser but the
				// `contentVersionHistory` doesn't expose it; default to empty path.
				profilePicture: { path: '' },
			};

			return {
				version: node.number ?? 0,
				isLatestVersion: index === 0,
				isNew: (node.number ?? 0) === 1,
				comment: node.message ?? '',
				contentType: 'attachment',
				creationDate: node.updatedTime ?? '',
				modifier,
			};
		});
	}, [data]);

	return {
		versions,
		loading: Boolean(loading && !data),
		error: error ?? null,
		refetch,
	};
};
